/*
 * Motor da calculadora de rendimento da Will (sem tela).
 *
 * Roda de dois jeitos, sem import/export de módulo:
 *   - no tema, como <script>: fica em window.WillCalculadora;
 *   - no Node, para teste: require('assets/will-calculadora-motor.js').
 *
 * Dados: as linhas de assets/will-rendimentos.json de UM produto (o chamador
 * filtra pelo handle antes). O motor usa só `cobertura_calculo_m2` na conta.
 * Para o cliente, a tela mostra sempre o valor publicado (rendimento_min_m2 /
 * rendimento_max_m2); o resultado devolve o publicado em
 * cobertura_publicada_m2_cada para isso.
 *
 * Regra do Gus (cor antes do total): sem preço de cor real, não há conta.
 * Não existe modo "a partir de" aqui.
 */
var WillCalculadora = (function () {
  'use strict';

  // Escrito com escape para não depender da codificação com que o navegador lê
  // este arquivo: "por demão".
  var PREFIXO_POR_DEMAO = 'por demão';

  // Folga para erro de ponto flutuante (ex.: 28.8 / 3 = 9.600000000000001).
  var FOLGA_M2 = 1e-7;
  var FOLGA_CUSTO = 1e-6;

  function texto(valor) {
    return valor == null ? '' : String(valor).trim();
  }

  // Casa "por demão", "por demão (taxa)", "por demão (1 demão)" — qualquer
  // base que comece com "por demão". NÃO casa "por embalagem (...)": esse
  // prefixo é outra família e não muda de comportamento aqui.
  function ehPorDemao(linha) {
    var b = texto(linha.base_rendimento).normalize('NFC').toLowerCase();
    return b.indexOf(PREFIXO_POR_DEMAO.normalize('NFC').toLowerCase()) === 0;
  }

  // "2 a 4" -> [2, 3, 4]; "2 a 3" -> [2, 3]; "2, podendo chegar a 3" -> [2, 3];
  // "3" -> [3]; "1" -> [1]; "" -> []. Pega o menor e o maior número do texto e
  // devolve todos os inteiros entre eles — mesma ideia de inteiros() em
  // 02-produtos/verificar-rendimentos.py.
  function faixaDeDemaos(numeroDemaos) {
    var achados = texto(numeroDemaos).match(/\d+/g) || [];
    if (achados.length === 0) return [];
    var numeros = achados.map(Number);
    var min = Math.min.apply(null, numeros);
    var max = Math.max.apply(null, numeros);
    var faixa = [];
    for (var n = min; n <= max; n++) faixa.push(n);
    return faixa;
  }

  // Linha de total fixo: numero_demaos vazio conta como 1.
  function demaosEfetivo(linha) {
    var t = texto(linha.numero_demaos);
    return t === '' ? 1 : Number(t);
  }

  function agruparPorEmbalagem(linhasProduto) {
    var grupos = [];
    var indice = {};
    for (var i = 0; i < linhasProduto.length; i++) {
      var linha = linhasProduto[i];
      var chave = linha.embalagem_display;
      if (!Object.prototype.hasOwnProperty.call(indice, chave)) {
        indice[chave] = grupos.length;
        grupos.push({ embalagem_display: chave, linhas: [] });
      }
      grupos[indice[chave]].linhas.push(linha);
    }
    return grupos;
  }

  /*
   * Descobre como uma embalagem responde ao número de demãos.
   * A decisão é por EMBALAGEM (handle + embalagem_display), não pelo produto:
   *   'porDemao'     -> 1 linha "por demão..." com faixa de 2+ valores (ex.:
   *                     "2 a 3", "2 a 4"); cobre cobertura_calculo_m2 / N, só
   *                     para N dentro da faixa. Vale para TODA embalagem com
   *                     essa linha, mesmo quando é a única linha do grupo —
   *                     é sempre a cobertura de UMA demão, nunca o total.
   *   'porDemaoFixa' -> 1 linha "por demão..." sem faixa (vazia) ou com um só
   *                     valor: divide pelo número da faixa (1 se vazia).
   *                     Ignora N recebido: não há escolha real de demãos.
   *   'opcoes'       -> várias linhas de total fixo com demãos diferentes, SEM
   *                     nenhuma linha "por demão"; N escolhe a linha (não
   *                     divide nada).
   *   'fixa'         -> 1 linha de total fixo, sem linha "por demão"; ignora N.
   *   'ambigua'      -> mais de uma linha "por demão" na mesma embalagem, ou a
   *                     tabela não deixa escolher uma linha só (não acontece
   *                     nos dados de 24/09/2026; se acontecer, o motor recusa
   *                     a conta em vez de adivinhar).
   *
   * Linhas "cobertura total N demãos" / "sistema completo N demãos" que
   * convivem com uma linha "por demão" na mesma embalagem NÃO entram na
   * conta: são só o publicado daquele N exato para exibição em outro lugar,
   * e a divisão por demão já cobre o mesmo N com o mesmo valor (a menos de
   * arredondamento de exibição). Ver card t_b8a90c0c.
   */
  function classificar(grupo) {
    var linhas = grupo.linhas;
    var porDemao = [];
    var outras = [];
    for (var i = 0; i < linhas.length; i++) {
      if (ehPorDemao(linhas[i])) porDemao.push(linhas[i]);
      else outras.push(linhas[i]);
    }

    if (porDemao.length > 1) return { tipo: 'ambigua' };
    if (porDemao.length === 1) {
      var linha = porDemao[0];
      var faixa = faixaDeDemaos(linha.numero_demaos);
      if (faixa.length <= 1) {
        return {
          tipo: 'porDemaoFixa',
          demaos: [],
          linha: linha,
          divisor: faixa.length === 1 ? faixa[0] : 1,
        };
      }
      return { tipo: 'porDemao', demaos: faixa, linha: linha };
    }

    var distintos = [];
    for (var j = 0; j < outras.length; j++) {
      var n = demaosEfetivo(outras[j]);
      if (!(n >= 1) || Math.floor(n) !== n) return { tipo: 'ambigua' };
      if (distintos.indexOf(n) === -1) distintos.push(n);
    }
    if (distintos.length === 1) {
      return outras.length === 1 ? { tipo: 'fixa', demaos: [] } : { tipo: 'ambigua' };
    }
    if (distintos.length !== outras.length) return { tipo: 'ambigua' };
    return { tipo: 'opcoes', demaos: distintos.sort(function (a, b) { return a - b; }) };
  }

  // Devolve { linha, cobertura } ou { fora: motivo }.
  function resolverEmbalagem(grupo, n) {
    var c = classificar(grupo);
    if (c.tipo === 'ambigua') return { fora: 'tabela_ambigua' };
    if (c.tipo === 'fixa') {
      var unica = grupo.linhas[0];
      return { linha: unica, cobertura: Number(unica.cobertura_calculo_m2) };
    }
    if (c.tipo === 'porDemaoFixa') {
      return { linha: c.linha, cobertura: Number(c.linha.cobertura_calculo_m2) / c.divisor };
    }
    if (n == null) return { fora: 'numero_demaos_obrigatorio' };
    if (c.demaos.indexOf(n) === -1) return { fora: 'demaos_fora_da_faixa' };
    if (c.tipo === 'porDemao') {
      return { linha: c.linha, cobertura: Number(c.linha.cobertura_calculo_m2) / n };
    }
    for (var i = 0; i < grupo.linhas.length; i++) {
      if (demaosEfetivo(grupo.linhas[i]) === n) {
        return { linha: grupo.linhas[i], cobertura: Number(grupo.linhas[i].cobertura_calculo_m2) };
      }
    }
    return { fora: 'demaos_fora_da_faixa' };
  }

  /*
   * Demãos que a tela pode oferecer para este produto (união das embalagens).
   * Lista vazia = produto sem escolha de demãos.
   */
  function demaosDisponiveis(linhasProduto) {
    var grupos = agruparPorEmbalagem(linhasProduto || []);
    var todas = [];
    for (var i = 0; i < grupos.length; i++) {
      var c = classificar(grupos[i]);
      var lista = c.demaos || [];
      for (var j = 0; j < lista.length; j++) {
        if (todas.indexOf(lista[j]) === -1) todas.push(lista[j]);
      }
    }
    return todas.sort(function (a, b) { return a - b; });
  }

  function temPreco(precos, embalagem) {
    return Object.prototype.hasOwnProperty.call(precos, embalagem) && precos[embalagem] != null;
  }

  // Centavos: inteiro >= 0. Preço quebrado (ex.: 89.9) quase sempre é real
  // passado no lugar de centavos, e daria total errado.
  function precoEmCentavosValido(p) {
    return typeof p === 'number' && isFinite(p) && p >= 0 && Math.floor(p) === p;
  }

  /*
   * Busca exaustiva da combinação mais barata que cobre a área.
   *
   * opcoes: [{ cobertura, preco, ... }], cobertura > 0, preco >= 0.
   * Contagens inteiras >= 0 de cada embalagem; cobre se soma(k_i * c_i) >= área.
   *
   * Limite de cada contagem: k_i <= ceil(área / c_i). Com mais que isso, a
   * embalagem i sozinha já cobre sem uma das unidades, e tirar essa unidade
   * não aumenta o custo (preço >= 0). Então existe um ótimo dentro do limite.
   * A última embalagem não é enumerada: recebe o mínimo que fecha a área.
   *
   * Complexidade no pior caso: O(prod_{i<m-1} (ceil(A / c_i) + 1)), com A a
   * área, c_i a cobertura e m o número de embalagens (2 a 4 por produto na
   * tabela). Com 4 embalagens isso é cúbico na área. Na prática a poda corta
   * quase tudo. Um ramo é cortado quando:
   *   (a) custo parcial + área que falta * (menor preço por m² entre as
   *       embalagens que faltam decidir) > melhor custo achado; esse limite
   *       nunca passa do custo real do ramo, então o corte não perde o ótimo;
   *   (b) ou o ramo, no máximo, empata no custo e já precisa de mais unidades
   *       que a melhor resposta (perderia no desempate). Vale porque o preço
   *       é inteiro (centavos).
   * O teto inicial (cobrir tudo com um tamanho só) liga a poda desde o começo.
   * Medido em 24/09/2026, 4 embalagens da Massa Acrílica (1,4 / 7,1 / 28,57 /
   * 32,86 m²), até 20.000 m²: preço realista < 1 ms; preço exatamente
   * proporcional (só empates) < 10 ms; quase proporcional (±1 centavo) ~60 ms.
   *
   * Empate de custo: fica a que usa menos embalagens; depois, a que sobra menos área.
   */
  function melhorCombinacao(opcoes, area) {
    var ordem = opcoes.slice().sort(function (a, b) { return b.cobertura - a.cobertura; });
    var m = ordem.length;

    var precoPorM2Min = new Array(m);
    for (var i = m - 1; i >= 0; i--) {
      var taxa = ordem[i].preco / ordem[i].cobertura;
      precoPorM2Min[i] = i === m - 1 ? taxa : Math.min(taxa, precoPorM2Min[i + 1]);
    }

    var contagem = new Array(m);
    for (var z = 0; z < m; z++) contagem[z] = 0;
    var melhor = null;

    function registrar(custo, unidades, coberto) {
      var ganha =
        melhor === null ||
        custo < melhor.custo ||
        (custo === melhor.custo &&
          (unidades < melhor.unidades ||
            (unidades === melhor.unidades && coberto < melhor.coberto - FOLGA_M2)));
      if (ganha) {
        melhor = { custo: custo, unidades: unidades, coberto: coberto, contagem: contagem.slice() };
      }
    }

    function buscar(i, coberto, custo, unidades) {
      var falta = area - coberto;
      if (falta <= FOLGA_M2) {
        registrar(custo, unidades, coberto);
        return;
      }
      if (i === m) return;

      var o = ordem[i];
      // Menor contagem desta embalagem que fecha sozinha o que falta.
      var maximo = Math.ceil((falta - FOLGA_M2) / o.cobertura);

      if (melhor !== null) {
        var custoMinimo = custo + falta * precoPorM2Min[i];
        if (custoMinimo > melhor.custo + FOLGA_CUSTO) return;
        // Aqui o ramo no máximo empata no custo. Só vale seguir se ainda puder
        // ganhar no desempate: `maximo` é o mínimo de unidades que falta, porque
        // ordem[i] é a maior cobertura entre as que faltam decidir.
        if (custoMinimo >= melhor.custo - FOLGA_CUSTO && unidades + maximo > melhor.unidades) return;
      }
      // A última embalagem não varia: recebe o máximo, e buscar() confere se cobriu.
      var minimo = i === m - 1 ? maximo : 0;
      for (var k = maximo; k >= minimo; k--) {
        contagem[i] = k;
        buscar(i + 1, coberto + k * o.cobertura, custo + k * o.preco, unidades + k);
      }
      contagem[i] = 0;
    }

    // Teto inicial para a poda: cobrir tudo com um tamanho só.
    for (var s = 0; s < m; s++) {
      contagem[s] = Math.max(1, Math.ceil((area - FOLGA_M2) / ordem[s].cobertura));
      registrar(contagem[s] * ordem[s].preco, contagem[s], contagem[s] * ordem[s].cobertura);
      contagem[s] = 0;
    }
    buscar(0, 0, 0, 0);
    if (melhor === null) return null;

    var combinacao = [];
    for (var j = 0; j < m; j++) {
      if (melhor.contagem[j] > 0) {
        combinacao.push({
          embalagem_display: ordem[j].embalagem_display,
          quantidade_embalagens: melhor.contagem[j],
          cobertura_usada_m2_cada: ordem[j].cobertura,
          cobertura_publicada_m2_cada: ordem[j].coberturaPublicada,
        });
      }
    }
    return {
      combinacao: combinacao,
      areaCobertaTotal_m2: Math.round(melhor.coberto * 1e6) / 1e6,
      custoTotalCentavos: melhor.custo,
    };
  }

  /*
   * calcular({ linhasProduto, areaM2, numeroDemaos, precos })
   *   linhasProduto  linhas de will-rendimentos.json de UM handle
   *   areaM2         número > 0
   *   numeroDemaos   inteiro, ou null/undefined se o produto não tem escolha
   *   precos         { [embalagem_display]: precoEmCentavos } da cor escolhida
   *
   * Sucesso: { ok: true, combinacao, areaCobertaTotal_m2, custoTotalCentavos }
   * Sem conta: { ok: false, motivo } — nunca lança exceção por caso esperado.
   *   sem_cor_selecionada        precos vazio, ou sem preço de embalagem da tabela
   *   produto_sem_tabela         linhasProduto vazio
   *   linhas_de_varios_produtos  linhasProduto mistura handles
   *   preco_invalido             preço de embalagem da tabela não é inteiro >= 0 (centavos)
   *   area_invalida              areaM2 não é número > 0
   *   numero_demaos_obrigatorio  embalagem com preço depende de N e N não veio
   *   tabela_ambigua             a tabela não deixa escolher uma linha só
   *   nenhuma_embalagem_elegivel nenhuma embalagem com preço serve para esse N
   */
  function calcular(entrada) {
    var e = entrada || {};
    var linhasProduto = e.linhasProduto;
    var precos = e.precos;

    // Cor antes do total: esta checagem vem primeiro, sempre.
    if (!precos || typeof precos !== 'object' || Object.keys(precos).length === 0) {
      return { ok: false, motivo: 'sem_cor_selecionada' };
    }
    if (!Array.isArray(linhasProduto) || linhasProduto.length === 0) {
      return { ok: false, motivo: 'produto_sem_tabela' };
    }
    for (var h = 1; h < linhasProduto.length; h++) {
      if (linhasProduto[h].handle !== linhasProduto[0].handle) {
        return { ok: false, motivo: 'linhas_de_varios_produtos' };
      }
    }

    var grupos = agruparPorEmbalagem(linhasProduto);
    var comPreco = [];
    for (var g = 0; g < grupos.length; g++) {
      if (temPreco(precos, grupos[g].embalagem_display)) comPreco.push(grupos[g]);
    }
    if (comPreco.length === 0) return { ok: false, motivo: 'sem_cor_selecionada' };
    for (var p = 0; p < comPreco.length; p++) {
      if (!precoEmCentavosValido(precos[comPreco[p].embalagem_display])) {
        return { ok: false, motivo: 'preco_invalido' };
      }
    }

    var area = e.areaM2;
    if (typeof area !== 'number' || !isFinite(area) || area <= 0) {
      return { ok: false, motivo: 'area_invalida' };
    }

    var n = e.numeroDemaos;
    if (typeof n !== 'number' || !isFinite(n) || n < 1 || Math.floor(n) !== n) n = null;

    var opcoes = [];
    for (var i = 0; i < comPreco.length; i++) {
      var r = resolverEmbalagem(comPreco[i], n);
      if (r.fora === 'tabela_ambigua' || r.fora === 'numero_demaos_obrigatorio') {
        return { ok: false, motivo: r.fora };
      }
      if (r.fora) continue; // demaos_fora_da_faixa: esta embalagem fica fora da conta
      if (!(r.cobertura > 0) || !isFinite(r.cobertura)) return { ok: false, motivo: 'tabela_ambigua' };
      opcoes.push({
        embalagem_display: comPreco[i].embalagem_display,
        cobertura: r.cobertura,
        coberturaPublicada: Number(r.linha.rendimento_min_m2),
        preco: precos[comPreco[i].embalagem_display],
      });
    }
    if (opcoes.length === 0) return { ok: false, motivo: 'nenhuma_embalagem_elegivel' };

    var melhor = melhorCombinacao(opcoes, area);
    if (melhor === null) return { ok: false, motivo: 'nenhuma_embalagem_elegivel' };
    return {
      ok: true,
      combinacao: melhor.combinacao,
      areaCobertaTotal_m2: melhor.areaCobertaTotal_m2,
      custoTotalCentavos: melhor.custoTotalCentavos,
    };
  }

  return {
    calcular: calcular,
    demaosDisponiveis: demaosDisponiveis,
    // expostas para teste
    _faixaDeDemaos: faixaDeDemaos,
    _melhorCombinacao: melhorCombinacao,
  };
})();

if (typeof module !== 'undefined' && module.exports) module.exports = WillCalculadora;
